var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { h, Fragment } from 'preact';
import { useState, useEffect, useRef, useMemo, useCallback } from 'preact/hooks';
import { FredhopperSearchProvider } from '../platforms/fredhopper';
import { FeaturedClothingHeader } from '../components/category/FeaturedClothingHeader';
import { BirdsnestFilterBar } from '../components/filters/BirdsnestFilterBar';
import { BirdsnestFilterDrawer } from '../components/filters/BirdsnestFilterDrawer';
import { MobileFilterDrawer } from '../components/filters/MobileFilterDrawer';
import { BirdsnestFilterTags } from '../components/filters/BirdsnestFilterTags';
import { BirdsnestProductGrid } from '../components/product/BirdsnestProductGrid';
import { BirdsnestPagination } from '../components/pagination/BirdsnestPagination';
import { BirdsnestInfiniteScroll } from '../components/infinite-scroll/BirdsnestInfiniteScroll';
import { PinnedSection } from '../components/product/PinnedSection';
import { useAlternativeLayout } from '../utils/liquidCustom';
import { STYLE_MATCHES_COLLECTION_ID } from '../utils/styleMatchesClient';
import styles from './BirdsnestDemoPage.module.scss';
import { useSwymInit } from '../utils/useSwymInit';
import { BirdsnestStyleMatches } from '../components/filters/StyleMatches';
import { Tabs } from '../components/tabs/Tabs';
import { DropdownTabs } from '../components/dropdown-tabs/DropdownTabs';
import { VideoCard } from '../components/product/VideoCard';
import { getBirdsnestTVDataQuery } from '../utils/shopify/queries';
import { parseFiltersFromURL, updateURLWithFilters, useFilterDrawerState } from '../utils/filters';
import { handlize, unhandlize, readTvTabSlugFromPath, buildTvPath } from '../utils/helpers';
import { useIsMobile } from '../utils/hooks';
import { FittingRoomContext, FittingRoomPopup, fittingRoomOpenPayloadToPopupProps, syncInstoreModeFromUrl, } from '../components/fitting-room';
import { trackViewItemList } from '../utils/elevar';
const getStaticConfig = (useAlternativeLayout) => {
    return {
        layout: {
            cardLayout: 'gridFour', // gridFour | gridTwo
            filterLayout: 'filterDrawer'
        },
        pagination: {
            type: 'pagination',
            itemsPerPage: 48,
            infiniteScrollMode: 'auto',
        },
        features: {
            searchBar: true,
            filters: true,
            sorting: useAlternativeLayout ? false : true,
            productCount: true,
            categoryHeader: true,
            modelToggle: useAlternativeLayout ? false : true
        },
        category: {
            title: "Women's Tops",
            description: "Packed with options for every look, our women's tops collection has it all. Discover classic women's shirts, comfy everyday basics, and bold, trendsetting designs. Your new wardrobe favourites are just waiting to be found.",
            pills: [
                { id: 'picked', label: 'TOPS PICKED FOR YOU', isActive: true },
                { id: 'tank', label: 'TANK TOPS' },
                { id: 'tunic', label: 'TUNIC TOPS' },
                { id: 'shirts', label: 'SHIRTS' },
                { id: 'linen', label: 'LINEN TOPS' },
                { id: 'longsleeve', label: 'LONG SLEEVE TOPS' },
                { id: 'blouses', label: 'BLOUSES' },
                { id: 'tees', label: 'TEES' },
                { id: 'camis', label: 'CAMIS' },
                { id: 'kimonos', label: 'KIMONOS' }
            ]
        },
        merchandising: [
            {
                id: 'promo-1',
                title: 'Tops picked\njust for you',
                ctaText: 'SHOP NOW',
                ctaLink: '/collections/tops-for-you',
                backgroundColor: '#FDF3F5',
                position: 3,
                image: 'https://cdn.shopify.com/s/files/1/0627/4731/2231/files/BIRDK-2247__Darknavy_003__35043.1759972031.jpg?v=1762924407?width=800'
            }
        ],
        styleMatches: {
            isLoading: false,
        },
    };
};
function enrichProductWithTvNode(p, node) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    const firstModel = (_c = (_b = (_a = node.modelsFeatured) === null || _a === void 0 ? void 0 : _a.references) === null || _b === void 0 ? void 0 : _b.nodes) === null || _c === void 0 ? void 0 : _c[0];
    const modelSize = (_e = (_d = firstModel === null || firstModel === void 0 ? void 0 : firstModel.field) === null || _d === void 0 ? void 0 : _d.value) !== null && _e !== void 0 ? _e : undefined;
    const relatedNodes = (_j = (_h = (_g = (_f = node.relatedProducts) === null || _f === void 0 ? void 0 : _f.references) === null || _g === void 0 ? void 0 : _g.nodes) === null || _h === void 0 ? void 0 : _h.filter((n) => n != null && typeof n === 'object' && 'title' in n && 'handle' in n)) !== null && _j !== void 0 ? _j : [];
    return Object.assign(Object.assign({}, p), { birdsnestTvStorefrontData: { modelSize, relatedProducts: relatedNodes } });
}
export function BirdsnestDemoPage() {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
    useSwymInit();
    const useAlternativeLayoutValue = useAlternativeLayout();
    const STATIC_CONFIG = getStaticConfig(useAlternativeLayoutValue);
    const isMobile = useIsMobile();
    const [layout] = useState({
        cardLayout: STATIC_CONFIG.layout.cardLayout,
        filterLayout: STATIC_CONFIG.layout.filterLayout
    });
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [fittingRoomSession, setFittingRoomSession] = useState(null);
    const handleOpenFittingRoomFromCard = useCallback((payload) => {
        setFittingRoomSession(fittingRoomOpenPayloadToPopupProps(payload));
    }, []);
    const closeFittingRoomPopup = useCallback(() => {
        setFittingRoomSession(null);
    }, []);
    const [isModelView, setIsModelView] = useState(() => {
        if (typeof window !== 'undefined') {
            const stored = sessionStorage.getItem('birdsnest-view-mode');
            return stored !== null ? stored === 'model' : true;
        }
        return true;
    });
    useEffect(() => {
        if (typeof window !== 'undefined') {
            sessionStorage.setItem('birdsnest-view-mode', isModelView ? 'model' : 'product');
        }
    }, [isModelView]);
    const [activeCategory, setActiveCategory] = useState(((_a = STATIC_CONFIG.category.pills[0]) === null || _a === void 0 ? void 0 : _a.id) || '');
    const isBirdnestTv = typeof window !== 'undefined' && !!((_b = window.liquidCustom) === null || _b === void 0 ? void 0 : _b.birdnestTv);
    const [activeFilters, setActiveFilters] = useState(() => {
        const fromUrl = parseFiltersFromURL();
        // Birdsnest TV: tab is encoded as a path segment. Seed activeFilters so the very
        // first Fredhopper request is already filtered. Gated on `isBirdnestTv` so we only
        // seed on real TV pages (not e.g. `/collections/<handle>/<tag>` URLs that share the
        // same path shape) — this relies on the inline script that sets
        // `window.liquidCustom.birdnestTv` running *before* the bundle. basetype is known
        // to be `text` for this facet, so hardcode the suffix; api.ts splits it back out.
        if (isBirdnestTv) {
            const slug = readTvTabSlugFromPath();
            if (slug !== 'all') {
                return Object.assign(Object.assign({}, fromUrl), { 'custom_birdsnest_tv::text': [unhandlize(slug)] });
            }
        }
        return fromUrl;
    });
    const [allProducts, setAllProducts] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    // TV tab lives in the path; helper returns 'all' on any non-TV path so this is
    // safe to call unconditionally (avoids depending on `window.liquidCustom.birdnestTv`
    // being set before the bundle loads).
    const [currentTabId, setCurrentTabId] = useState(readTvTabSlugFromPath);
    const collectionId = useMemo(() => {
        if (typeof window !== 'undefined') {
            const element = document.querySelector('#search-app');
            return (element === null || element === void 0 ? void 0 : element.getAttribute('data-collection-id')) || '';
        }
        return '';
    }, []);
    // Style Matches shows more recommendations per page than the standard PLP.
    const STYLE_MATCHES_PAGE_SIZE = 96;
    const itemsPerPage = collectionId === STYLE_MATCHES_COLLECTION_ID
        ? STYLE_MATCHES_PAGE_SIZE
        : STATIC_CONFIG.pagination.itemsPerPage;
    let initialQuery;
    let initialSort;
    if (typeof window !== 'undefined') {
        const urlParams = new URLSearchParams(window.location.search);
        const sortFromURL = urlParams.get('sort');
        const fullQueryString = window.location.search.startsWith('?')
            ? window.location.search.substring(1)
            : window.location.search;
        const qFromURL = urlParams.get('q') || undefined;
        initialQuery = qFromURL || undefined;
        initialSort = sortFromURL || undefined;
    }
    // Local sort state
    const [localSort, setLocalSort] = useState(initialSort || "relevance");
    const [currentQuery, setCurrentQuery] = useState(initialQuery || '');
    const providerRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const shouldScrollToTop = useRef(false);
    const mainContentRef = useRef(null);
    const [products, setProducts] = useState([]);
    const [filters, setFilters] = useState([]);
    const [meta, setMeta] = useState(null);
    const [sortOptions, setSortOptions] = useState([]);
    const trackingListId = useMemo(() => {
        if (collectionId) {
            return `fh_plp_${collectionId}`;
        }
        if (typeof window !== 'undefined') {
            const match = window.location.pathname.match(/\/collections\/([^/]+)/);
            if (match === null || match === void 0 ? void 0 : match[1]) {
                return `fh_plp_${match[1]}`;
            }
        }
        return 'fh_plp_all';
    }, [collectionId]);
    const trackedPLPKeyRef = useRef(null);
    useEffect(() => {
        if (loading || !products.length)
            return;
        const key = `${trackingListId}:${(meta === null || meta === void 0 ? void 0 : meta.currentPage) || 1}:${products.map((p) => p.id).join(',')}`;
        if (trackedPLPKeyRef.current === key)
            return;
        trackedPLPKeyRef.current = key;
        trackViewItemList(products, trackingListId);
    }, [loading, products, trackingListId, meta === null || meta === void 0 ? void 0 : meta.currentPage]);
    useEffect(() => {
        syncInstoreModeFromUrl();
    }, []);
    // Style Matches loading state — true while the Peacock API is still building
    // recommendations (response: `{ state: "building", percent_complete }`). Anything
    // below 100 means recommendations aren't ready yet.
    const isLoadingStyleMatches = typeof (meta === null || meta === void 0 ? void 0 : meta.styleMatchesPercentComplete) === 'number' &&
        meta.styleMatchesPercentComplete < 100;
    // Style Matches requires a logged-in customer (Peacock identifies them by
    // email). When the page is the Style Matches PLP but no customer email is
    // surfaced from Liquid, suppress every product-facing component and let
    // <BirdsnestStyleMatches> render its signed-out variant instead.
    const isStyleMatchesPage = ((_e = (_d = (_c = window.liquidCustom) === null || _c === void 0 ? void 0 : _c.styleMatches) === null || _d === void 0 ? void 0 : _d.length) !== null && _e !== void 0 ? _e : 0) > 0;
    const isCustomerLoggedIn = !!((_f = window.mrPLPConfig) === null || _f === void 0 ? void 0 : _f.customerEmail);
    const showOnlyStyleMatchesHero = isStyleMatchesPage && !isCustomerLoggedIn;
    if (!providerRef.current) {
        providerRef.current = new FredhopperSearchProvider();
    }
    const runSearch = (query, options) => __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c;
        if (!providerRef.current)
            return;
        setLoading(true);
        try {
            yield providerRef.current.search(query, options);
            const providerProducts = providerRef.current.getProducts();
            /* Birdsnest TV products have 2 extra attributes:
              - models_featured: list of Model metaobjects → model_size. (used in "Sizes featured" text)
              - related_products: product refs → title, handle, product_front_media (image metafield). (used in the product carousel)
              Because Fredhopper does not provide these 2 attributes from their API,
              We need to fetch the data from the Storefront API for these products and enrich the products with the data.
            */
            const birdsnestTVProductIds = typeof window !== 'undefined' && ((_a = window.liquidCustom) === null || _a === void 0 ? void 0 : _a.birdnestTv)
                ? providerProducts.map((p) => p.id.startsWith('gid://') ? p.id.split('/').pop() : p.id)
                : [];
            if (birdsnestTVProductIds.length > 0) {
                const { data: tvData } = yield getBirdsnestTVDataQuery(birdsnestTVProductIds);
                const nodes = (_b = tvData === null || tvData === void 0 ? void 0 : tvData.nodes) !== null && _b !== void 0 ? _b : [];
                const nodeById = new Map();
                for (const node of nodes) {
                    if (!(node === null || node === void 0 ? void 0 : node.id))
                        continue;
                    const id = (_c = node.id.split('/').pop()) !== null && _c !== void 0 ? _c : node.id;
                    nodeById.set(id, node);
                }
                const enrichedBirdsnestTVProducts = providerProducts.map((p) => {
                    const id = p.id.startsWith('gid://') ? p.id.split('/').pop() : p.id;
                    const node = nodeById.get(id);
                    if (!node)
                        return p;
                    return enrichProductWithTvNode(p, node);
                });
                setProducts(enrichedBirdsnestTVProducts);
            }
            else {
                setProducts(providerProducts);
            }
            setFilters(providerRef.current.getFilters());
            setMeta(providerRef.current.getMeta());
            setSortOptions(providerRef.current.getSortOptions());
        }
        catch (error) {
            console.error('Search failed:', error);
        }
        finally {
            setLoading(false);
        }
    });
    const { filterMetadataCache, orderedFilterItems } = useFilterDrawerState(filters, activeFilters);
    useEffect(() => {
        if (STATIC_CONFIG.pagination.type === 'infiniteScroll') {
            if ((meta === null || meta === void 0 ? void 0 : meta.currentPage) === 1) {
                setAllProducts(products);
                setCurrentPage(1);
            }
            else if ((meta === null || meta === void 0 ? void 0 : meta.currentPage) && meta.currentPage > currentPage) {
                setAllProducts(prev => [...prev, ...products]);
                setCurrentPage(meta.currentPage);
            }
        }
    }, [products, meta === null || meta === void 0 ? void 0 : meta.currentPage]);
    const initialMountRef = useRef(true);
    useEffect(() => {
        if (initialMountRef.current) {
            initialMountRef.current = false;
            if (typeof window !== 'undefined') {
                // Style Matches without a logged-in customer has nothing to fetch —
                // Peacock would reject the request — so skip the initial search and
                // let the signed-out hero render on its own.
                if (showOnlyStyleMatchesHero)
                    return;
                const urlParams = new URLSearchParams(window.location.search);
                const pageFromURL = urlParams.get('page');
                const pageNumber = pageFromURL ? parseInt(pageFromURL, 10) : 1;
                runSearch(initialQuery || '', {
                    page: pageNumber,
                    pageSize: itemsPerPage,
                    sort: localSort || undefined,
                    filters: activeFilters,
                    collectionId: collectionId
                });
            }
        }
    }, []); // Only run on mount
    const handleSortChange = (sortId) => {
        setLocalSort(sortId);
        setCurrentPage(1);
        setAllProducts([]);
        updateURLWithFilters(activeFilters, { sort: sortId, page: 1 });
        runSearch(currentQuery || '', {
            page: 1,
            pageSize: itemsPerPage,
            sort: sortId,
            filters: activeFilters,
            collectionId: collectionId
        });
    };
    const handleApplyFilters = (newFilters, nextSort = localSort) => {
        setActiveFilters(newFilters || {});
        setLocalSort(nextSort);
        setCurrentPage(1);
        setAllProducts([]);
        updateURLWithFilters(newFilters || {}, { sort: nextSort || undefined, page: 1 });
        runSearch(currentQuery || '', {
            page: 1,
            pageSize: itemsPerPage,
            sort: nextSort || undefined,
            filters: newFilters || {},
            collectionId: collectionId
        });
    };
    // Birdsnest TV tabs are a thin wrapper around the `custom_birdsnest_tv` Fredhopper
    // filter, but the active tab is encoded as a path segment
    // (e.g. /collections/birdsnest-tv/live-replay) instead of a query param. tabId
    // is the handlised slug; un-handlise to recover the Fredhopper facet value.
    // The filterId carries the `::<basetype>` suffix so buildLocationQuery in api.ts
    // knows to emit `attr=value` for text/single-select facets — see
    // src/platforms/fredhopper/README.md.
    const handleTabChange = (tabId) => {
        var _a;
        const basetype = (_a = tvBasetypeRef.current) !== null && _a !== void 0 ? _a : 'text';
        setCurrentTabId(tabId);
        setCurrentPage(1);
        setAllProducts([]);
        const filterKey = `custom_birdsnest_tv::${basetype}`;
        const next = Object.assign({}, activeFilters);
        if (tabId === 'all') {
            delete next[filterKey];
            delete next.custom_birdsnest_tv;
        }
        else {
            next[filterKey] = [unhandlize(tabId)];
        }
        setActiveFilters(next);
        // URL: TV tab in pathname, everything else (other filters / sort) in the query string.
        const filtersForURL = Object.assign({}, next);
        delete filtersForURL[filterKey];
        delete filtersForURL.custom_birdsnest_tv;
        updateURLWithFilters(filtersForURL, { sort: localSort || undefined, page: 1 });
        const newPath = buildTvPath(tabId);
        if (window.location.pathname !== newPath) {
            window.history.pushState({}, '', newPath + window.location.search);
        }
        runSearch(currentQuery || '', {
            page: 1,
            pageSize: itemsPerPage,
            sort: localSort || undefined,
            filters: next,
            collectionId: collectionId,
        });
    };
    const handleClearAllFilters = () => {
        const cleared = {};
        setActiveFilters(cleared);
        setCurrentPage(1);
        setAllProducts([]);
        updateURLWithFilters(cleared, { sort: localSort || undefined, page: 1 });
        runSearch(currentQuery || '', {
            page: 1,
            pageSize: itemsPerPage,
            sort: localSort || undefined,
            filters: cleared,
            collectionId: collectionId
        });
    };
    const handleRemoveFilter = (filterId, optionId) => {
        setActiveFilters(prev => {
            const currentValues = prev[filterId] || [];
            const nextValues = currentValues.filter(value => value !== optionId);
            const nextFilters = Object.assign({}, prev);
            if (nextValues.length > 0) {
                nextFilters[filterId] = nextValues;
            }
            else {
                delete nextFilters[filterId];
            }
            setCurrentPage(1);
            setAllProducts([]);
            updateURLWithFilters(nextFilters, { sort: localSort || undefined, page: 1 });
            runSearch(currentQuery || '', {
                page: 1,
                pageSize: itemsPerPage,
                sort: localSort || undefined,
                filters: nextFilters,
                collectionId: collectionId
            });
            return nextFilters;
        });
    };
    const holdMainContentHeight = () => {
        const element = mainContentRef.current;
        const height = element === null || element === void 0 ? void 0 : element.offsetHeight;
        if (!element || !height)
            return;
        element.style.minHeight = `${height}px`;
    };
    const releaseMainContentHeight = () => {
        if (mainContentRef.current) {
            mainContentRef.current.style.minHeight = '';
        }
    };
    useEffect(() => {
        if (!loading && shouldScrollToTop.current) {
            window.setTimeout(() => {
                releaseMainContentHeight();
                shouldScrollToTop.current = false;
            }, 700);
        }
    }, [loading]);
    const handlePageChange = (page) => {
        shouldScrollToTop.current = true;
        holdMainContentHeight();
        if (window.scrollY > 1) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        updateURLWithFilters(activeFilters, { sort: localSort || undefined, page });
        runSearch(currentQuery, {
            page,
            pageSize: itemsPerPage,
            sort: localSort || undefined,
            filters: activeFilters,
            collectionId: collectionId
        });
    };
    const handleLoadMore = () => {
        if (!meta || loading)
            return;
        const nextPage = (meta.currentPage || 1) + 1;
        if (nextPage <= meta.totalPages) {
            runSearch(currentQuery || '', {
                page: nextPage,
                pageSize: itemsPerPage,
                sort: localSort || undefined,
                filters: activeFilters,
                collectionId: collectionId
            });
        }
    };
    const handleCategoryClick = (categoryId) => {
        setActiveCategory(categoryId);
    };
    // Style Matches tab IDs map directly to the backend `matches-type` allowlist
    // (`dresses-and-jumpsuits`, `all-tops`, `all-bottoms`, `other`); the default
    // `'all'` tab sends no `matches-type` and Peacock returns the full list.
    // 'matches-type' parameters is a Peacock API parameter that is set by Birdsnest to filter style matches products by type.
    const handleStyleMatchesTabChange = (tabId) => {
        setCurrentTabId(tabId);
        setCurrentPage(1);
        setAllProducts([]);
        runSearch(currentQuery || '', {
            page: 1,
            pageSize: itemsPerPage,
            sort: localSort || undefined,
            filters: activeFilters,
            collectionId: collectionId,
            matchesType: tabId === 'all' ? undefined : tabId,
        });
    };
    useEffect(() => {
        const handlePopState = () => {
            var _a;
            const filtersFromURL = parseFiltersFromURL();
            // Birdsnest TV: tab lives in pathname, not query string. Re-derive on back/forward.
            if (isBirdnestTv) {
                const slug = readTvTabSlugFromPath();
                setCurrentTabId(slug);
                if (slug !== 'all') {
                    filtersFromURL[`custom_birdsnest_tv::${(_a = tvBasetypeRef.current) !== null && _a !== void 0 ? _a : 'text'}`] = [unhandlize(slug)];
                }
            }
            setActiveFilters(filtersFromURL);
            const urlParams = new URLSearchParams(window.location.search);
            const sortFromURL = urlParams.get('sort');
            const pageFromURL = parseInt(urlParams.get('page') || '1');
            setLocalSort(sortFromURL || 'relevance');
            runSearch(currentQuery || '', {
                page: pageFromURL,
                pageSize: itemsPerPage,
                sort: sortFromURL || undefined,
                filters: filtersFromURL,
                collectionId: collectionId
            });
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [currentQuery, runSearch]);
    // Once Fredhopper has returned the `custom_birdsnest_tv` facet (typically on the
    // unfiltered "ALL" load), cache its options + basetype. After the user selects a tab,
    // Fredhopper omits the facet from subsequent responses, so without these caches the
    // tab list collapses to just "ALL" and handleTabChange loses the basetype context.
    const cachedTvTabsRef = useRef([]);
    const tvBasetypeRef = useRef(undefined);
    const birdsnestTvTabs = useMemo(() => {
        var _a, _b;
        const tvFilter = filters.find(f => f.id === 'custom_birdsnest_tv');
        if ((_a = tvFilter === null || tvFilter === void 0 ? void 0 : tvFilter.options) === null || _a === void 0 ? void 0 : _a.length) {
            // Tab id is the handlised form (URL-friendly slug). The original Fredhopper
            // value is recovered by un-handlising at filter-apply time.
            cachedTvTabsRef.current = tvFilter.options.map(option => ({
                id: handlize(option.id),
                label: option.label,
            }));
        }
        if (Array.isArray(tvFilter === null || tvFilter === void 0 ? void 0 : tvFilter.customAttributes)) {
            const basetype = (_b = tvFilter.customAttributes
                .find(a => (a === null || a === void 0 ? void 0 : a.name) === 'basetype')) === null || _b === void 0 ? void 0 : _b.value;
            if (basetype)
                tvBasetypeRef.current = basetype;
        }
        return [{ id: 'all', label: 'all' }, ...cachedTvTabsRef.current];
    }, [filters]);
    const categoryPills = STATIC_CONFIG.category.pills.map(pill => (Object.assign(Object.assign({}, pill), { isActive: pill.id === activeCategory })));
    const pinnedSections = useMemo(() => {
        var _a;
        const applied = (_a = meta === null || meta === void 0 ? void 0 : meta.merchandisingApplied) !== null && _a !== void 0 ? _a : [];
        return applied
            .filter((item) => { var _a; return (_a = item.pinnedProducts) === null || _a === void 0 ? void 0 : _a.length; })
            .map((item) => {
            var _a, _b, _c;
            return ({
                id: (_c = (_b = (_a = item.rules) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.id) !== null && _c !== void 0 ? _c : '',
                products: item.pinnedProducts,
            });
        });
    }, [meta]);
    return (h(FittingRoomContext.Provider, { value: handleOpenFittingRoomFromCard },
        h(Fragment, null,
            ((_g = window.liquidCustom) === null || _g === void 0 ? void 0 : _g.birdnestTv) ? (h("div", { class: `${styles.birdsnestPage} ${styles.birdsnestPageTV}` },
                h(Fragment, null,
                    h("div", { class: styles.hiddenMobile },
                        h(Tabs, { tabs: birdsnestTvTabs, activeId: currentTabId, onChange: handleTabChange })),
                    h("div", { class: `${styles.hiddenDesktop} ${styles.mobileDropdownTabs}` },
                        h(DropdownTabs, { tabs: birdsnestTvTabs, activeId: currentTabId, onChange: handleTabChange }))),
                loading ?
                    h("div", { class: styles.loadingContainer },
                        h("div", { class: styles.spinner }))
                    : (h(Fragment, null,
                        (products === null || products === void 0 ? void 0 : products.length) > 0 ? h("div", { class: styles.videoWrapperGrid }, products === null || products === void 0 ? void 0 : products.map((product) => (h(VideoCard, { key: product.id, product: product })))) :
                            h("p", { class: styles.textCenter }, "No videos found."),
                        meta && (h(BirdsnestPagination, { meta: meta, onChange: handlePageChange })))))) : (h("div", { class: `${styles.birdsnestPage} ${isLoadingStyleMatches ? styles.birdsnestPageLoading : ''} ` },
                !showOnlyStyleMatchesHero && pinnedSections.map((section) => (h(PinnedSection, { key: section.id, section: section, title: section.id === 'plp_outfits' ? 'Featured Outfits' : 'Featured Block', collectionId: collectionId }))),
                ((_j = (_h = window.liquidCustom) === null || _h === void 0 ? void 0 : _h.styleMatches) === null || _j === void 0 ? void 0 : _j.length) > 0 && (h(Fragment, null,
                    h(BirdsnestStyleMatches, { meta: meta }))),
                !showOnlyStyleMatchesHero && ((_l = (_k = window.liquidCustom) === null || _k === void 0 ? void 0 : _k.styleMatches) === null || _l === void 0 ? void 0 : _l.length) > 0 && !isLoadingStyleMatches && (h(Fragment, null,
                    h("div", { class: styles.hiddenMobile },
                        h(Tabs, { tabs: [
                                { id: 'all', label: 'best matches' },
                                { id: 'dresses-and-jumpsuits', label: 'dresses & jumpsuits' },
                                { id: 'all-tops', label: 'tops' },
                                { id: 'all-bottoms', label: 'bottoms' },
                                { id: 'other', label: 'other' },
                            ], activeId: currentTabId, onChange: handleStyleMatchesTabChange })),
                    h("div", { class: `${styles.hiddenDesktop} ${styles.mobileDropdownTabs} ${styles.styleMatchesDropdown}` },
                        h(DropdownTabs, { tabs: [
                                { id: 'all', label: 'best matches' },
                                { id: 'dresses-and-jumpsuits', label: 'dresses & jumpsuits' },
                                { id: 'all-tops', label: 'tops' },
                                { id: 'all-bottoms', label: 'bottoms' },
                                { id: 'other', label: 'other' },
                            ], activeId: currentTabId, onChange: handleStyleMatchesTabChange })))),
                !showOnlyStyleMatchesHero && !isLoadingStyleMatches && h("div", { class: styles.mainContent, ref: mainContentRef },
                    h(FeaturedClothingHeader, { collectionId: collectionId }),
                    (STATIC_CONFIG.features.filters || STATIC_CONFIG.features.sorting) && meta && (h(BirdsnestFilterBar, { totalResults: meta.totalResults, sortOptions: sortOptions, currentSort: localSort, onSortChange: handleSortChange, onFilterClick: () => setIsFilterOpen(true), showModelToggle: STATIC_CONFIG.features.modelToggle, isModelView: isModelView, onModelToggle: () => setIsModelView(!isModelView), hideSort: isMobile, runSearch: runSearch, query: currentQuery, filters: activeFilters, pageSize: itemsPerPage })),
                    STATIC_CONFIG.features.filters && Object.keys(activeFilters).length > 0 && (h(BirdsnestFilterTags, { activeFilters: activeFilters, filterMetadataCache: filterMetadataCache.current, onRemoveFilter: handleRemoveFilter, onClearAll: handleClearAllFilters })),
                    STATIC_CONFIG.pagination.type === 'infiniteScroll' ? (h(BirdsnestInfiniteScroll, { products: allProducts, loading: loading, layout: layout, meta: meta, mode: STATIC_CONFIG.pagination.infiniteScrollMode, hasMore: meta ? meta.currentPage < meta.totalPages : false, onLoadMore: handleLoadMore, merchandisingTiles: ((_m = window.liquidCustom) === null || _m === void 0 ? void 0 : _m.merchandising) ? (_o = window.liquidCustom) === null || _o === void 0 ? void 0 : _o.merchandising : [], isModelView: isModelView, trackingListId: trackingListId })) : (h(Fragment, null,
                        h(BirdsnestProductGrid, { products: products, loading: loading, meta: meta, layout: layout, merchandisingTiles: ((_p = window.liquidCustom) === null || _p === void 0 ? void 0 : _p.merchandising) ? (_q = window.liquidCustom) === null || _q === void 0 ? void 0 : _q.merchandising : [], isModelView: isModelView, trackingListId: trackingListId }),
                        meta && (h(BirdsnestPagination, { meta: meta, onChange: handlePageChange }))))),
                !showOnlyStyleMatchesHero && STATIC_CONFIG.features.filters && !isMobile && (h(BirdsnestFilterDrawer, { orderedFilterItems: orderedFilterItems, activeFilters: activeFilters, onApply: handleApplyFilters, onClearAll: handleClearAllFilters, isOpen: isFilterOpen, onClose: () => setIsFilterOpen(false) })),
                !showOnlyStyleMatchesHero && STATIC_CONFIG.features.filters && isMobile && (h(MobileFilterDrawer, { orderedFilterItems: orderedFilterItems, sortOptions: sortOptions, currentSort: localSort, onSortChange: handleSortChange, activeFilters: activeFilters, onApply: handleApplyFilters, onClearAll: handleClearAllFilters, isOpen: isFilterOpen, onClose: () => setIsFilterOpen(false), runSearch: runSearch, query: currentQuery, pageSize: itemsPerPage })))),
            fittingRoomSession ? (h(FittingRoomPopup, Object.assign({ open: true, onClose: closeFittingRoomPopup }, fittingRoomSession))) : null)));
}
